import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Check, X, Zap, Gift, Truck, MessageCircle, Sparkles, Shield, Star, Crown, Phone, Clock, ChevronDown, CreditCard, LogIn, Smartphone } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { VillatechTestimonialsSection } from '@/components/ui/villatech-testimonials';
import { MembershipCardForm } from '@/components/ui/membership-card-form';
import { MembershipBenefitsShowcase } from '@/components/ui/membership-benefits-showcase';
import { HeroSectionWithParallax } from '@/components/membresias/HeroSectionWithParallax';
import { ProductDiscountCalculator } from '@/components/ui/product-discount-calculator';
import { InfiniteCategorySlider } from '@/components/ui/infinite-category-slider';
import { CustomerSavingsCalculator } from '@/components/ui/customer-savings-calculator';
import { useFacebookPixel } from '@/hooks/useFacebookPixel';

// Logos de membresías
import logoBronce from '@/assets/membresia-logos/logo-bronce-new.png';
import logoPlata from '@/assets/membresia-logos/logo-plata-new.png';
import logoOro from '@/assets/membresia-logos/logo-oro-new.png';

// Logos para tarjetas de crédito
import cardLogoBronce from '@/assets/membresia-logos/card-logo-bronce.png';
import cardLogoPlata from '@/assets/membresia-logos/card-logo-plata.png';
import cardLogoOro from '@/assets/membresia-logos/card-logo-oro.png';
import villatechTextLogo from '@/assets/membresia-logos/villatech-text-logo.png';

// Imágenes de tarjetas de membresía
import cardBronce from '@/assets/membresia-logos/card-bronce.png';
import cardSilver from '@/assets/membresia-logos/card-silver.png';
import cardGold from '@/assets/membresia-logos/card-gold.png';

// Productos reales VillaTech
import droneE88 from '@/assets/products/drone-e88.jpg';
import droneK12Max from '@/assets/products/drone-k12max.jpg';
import gameProjector from '@/assets/products/game-projector.jpg';
import smartKeyboardCase from '@/assets/products/smart-keyboard-case.jpg';
import samsungLeatherCase from '@/assets/products/samsung-leather-case.jpg';
import magneticCharger from '@/assets/products/magnetic-charger.jpg';
import metalStand from '@/assets/products/metal-stand.jpg';
import fengaiC60 from '@/assets/products/fengai-c60.jpg';
import phoneBronceClear from '@/assets/phone-bronce-clear-case.png';
import phonePlataMagsafe from '@/assets/phones/phone-plata-magsafe.png';
import phoneOroOtterbox from '@/assets/phones/phone-oro-otterbox.png';

// Imágenes de pasos
import stepRegister from '@/assets/step-register-combined.png';
import stepWhatsapp from '@/assets/step-whatsapp-contact.png';
import stepBenefits from '@/assets/steps/step-benefits.png';

// Hook para el countdown
const useCountdown = (targetDate: Date) => {
  const calculateTimeLeft = () => {
    const difference = targetDate.getTime() - new Date().getTime();
    
    if (difference <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }
    
    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60),
    };
  };

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  return timeLeft;
};

const Membresias: React.FC = () => {
  const navigate = useNavigate();
  const isEn = false;

  // Fecha límite: 31 de enero 2026 a las 23:59:59
  const targetDate = new Date('2026-01-31T23:59:59');
  const timeLeft = useCountdown(targetDate);

  // State for the card form modal
  const [selectedPlan, setSelectedPlan] = useState<any>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isTimelineOpen, setIsTimelineOpen] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly');
  const [selectedDiscountPlan, setSelectedDiscountPlan] = useState<'bronce' | 'plata' | 'oro'>('oro');

  // Translations
  const t = {
    heroOffer: isEn ? '🔥 Launch offer – until January 31, 2026' : '🔥 Oferta de lanzamiento – hasta el 31 de enero 2026',
    heroTitle1: isEn ? 'Activate your membership' : 'Activá tu membresía',
    heroTitle2: isEn ? 'INSTANT DISCOUNT' : 'DESCUENTO INMEDIATO',
    heroTitle3: isEn ? 'on any product' : 'de cualquier producto',
    heroSubtitle: isEn ? 'Receive phone protection every month and discounts on our entire store' : 'Recibí protección de tu celular todos los meses y descuentos en toda nuestra tienda',
    heroHighlight: isEn ? 'phone protection every month' : 'protección de tu celular todos los meses',
    countdownLabel: isEn ? '⏰ Offer ends in:' : '⏰ La oferta termina en:',
    days: isEn ? 'Days' : 'Días',
    hours: isEn ? 'Hours' : 'Horas',
    min: isEn ? 'Min' : 'Min',
    sec: isEn ? 'Sec' : 'Seg',
    ctaButton: isEn ? '👉 GET MY DISCOUNT NOW' : '👉 QUIERO MI DESCUENTO AHORA',
    ctaRegister: isEn ? 'Activate Membership' : 'Activar Membresía',
    ctaLogin: isEn ? 'Login' : 'Iniciar Sesión',
    priceFrom: isEn ? 'From' : 'Desde',
    perMonth: isEn ? 'per month' : 'por mes',
    seeHow: isEn ? 'See how it works' : 'Ver cómo funciona',
    howItWorks: isEn ? 'How does it work?' : '¿Cómo funciona?',
    superEasy: isEn ? 'Super easy' : 'Super fácil',
    stepsDesc: isEn ? '3 simple steps to start enjoying your benefits' : '3 pasos simples para empezar a disfrutar tus beneficios',
    step1Title: isEn ? 'Register' : 'Registrate',
    step1Desc: isEn ? 'From home or visit us in store' : 'Desde tu casa o visitanos en tienda',
    step2Title: isEn ? 'We contact you' : 'Te contactamos',
    step2Desc: isEn ? 'Our team reaches out via WhatsApp to coordinate your activation' : 'Nuestro equipo te contacta por WhatsApp para coordinar tu activación',
    step3Title: isEn ? 'Enjoy your benefits' : 'Recibí tus beneficios',
    step3Desc: isEn ? 'Start receiving monthly benefits from your plan' : 'Empezá a recibir los beneficios mensuales de tu plan',
    choosePlan: isEn ? 'Choose your plan and activate it now' : 'Elegí tu plan y activalo ahora',
    launchPrice: isEn ? '🔥 Launch price until January 31' : '🔥 Precio de lanzamiento hasta el 31 de enero',
    only50Gold: isEn ? 'Only 50 GOLD memberships available' : 'Solo 50 membresías GOLD disponibles',
    monthly: isEn ? 'Monthly' : 'Mensual',
    annual: isEn ? 'Annual (2 months free)' : 'Anual (2 meses gratis)',
    popular: isEn ? 'Most popular' : 'Más popular',
    vipExperience: isEn ? 'VIP Experience' : 'Experiencia VIP',
    choosePlanBtn: isEn ? 'Choose' : 'Elegir',
    comparePlans: isEn ? 'Compare Plans' : 'Comparar Planes',
    yourDiscounts: isEn ? 'Your discounts with membership' : 'Tus descuentos con membresía',
    selectPlan: isEn ? 'Select a plan to see your discounts:' : 'Seleccioná un plan para ver tus descuentos:',
    testimonials: isEn ? 'What our members say' : 'Lo que dicen nuestros miembros',
    faqs: isEn ? 'Frequently Asked Questions' : 'Preguntas Frecuentes',
    ready: isEn ? 'Ready to start?' : '¿Listo para empezar?',
    activateNow: isEn ? 'Activate your membership now' : 'Activá tu membresía ahora',
    // Card section
    yourVillatechCard: isEn ? 'Types of Memberships' : 'Tipos de Membresías',
    chooseLevel: isEn ? 'Choose the level that best suits you' : 'Elegí el nivel que mejor se ajuste a vos',
    clickToActivate: isEn ? 'Click to activate' : 'Click para activar',
    hideBenefits: isEn ? 'Hide Benefits' : 'Ocultar Beneficios',
    seeWhatIncludes: isEn ? '✨ See what each plan includes' : '✨ Ver qué incluye cada plan',
    membership: isEn ? 'Membership' : 'Membresía',
    // Plans section
    chooseMembership: isEn ? 'Choose your membership' : 'Elegí tu membresía',
    promoUntil: isEn ? 'Promotional prices until January 31, 2026' : 'Precios promocionales hasta el 31 de enero 2026',
    save: isEn ? 'You save' : 'Ahorrás',
    activateMembership: isEn ? 'Activate Membership' : 'Activar Membresía',
    // Plan names and subtitles
    bronze: isEn ? 'Bronze' : 'Bronce',
    silver: isEn ? 'Silver' : 'Plata',
    gold: isEn ? 'Gold' : 'Oro',
    basicProtection: isEn ? 'Basic Protection' : 'Protección Básica',
    completeProtection: isEn ? 'Complete Protection' : 'Protección Completa',
    vipTotal: isEn ? 'VIP Total' : 'VIP Total',
    // Comparison table
    comparePlansTitle: isEn ? 'Compare the plans' : 'Compará los planes',
    benefit: isEn ? 'Benefit' : 'Beneficio',
    // Products section
    seeHowMuchYouSave: isEn ? 'See how much you save' : 'Mirá cuánto ahorrás',
    selectPlanSeeDiscounts: isEn ? 'Select a plan and see the discounted prices' : 'Seleccioná un plan y mirá los precios con descuento',
    youSave: isEn ? 'You save' : 'Ahorrás',
    // VIP section
    exclusiveGoldPlan: isEn ? 'Exclusive Gold Plan' : 'Exclusivo Plan Oro',
    vipDirectAccess: isEn ? 'VIP Direct Access' : 'Acceso VIP Directo',
    vipDescription: isEn ? "You don't go through the counter," : 'No pasás por mostrador,',
    vipHighlight: isEn ? 'you talk directly to the boss' : 'hablás directo con quien manda',
    vipSubtext: isEn ? 'Personalized WhatsApp with the administrator to solve EVERYTHING.' : 'WhatsApp personalizado con el administrador para resolver TODO.',
    directWhatsapp: isEn ? 'Direct WhatsApp' : 'WhatsApp Directo',
    exclusiveLine: isEn ? 'Exclusive line with administrator' : 'Línea exclusiva con administrador',
    fastResponse: isEn ? 'Fast Response' : 'Respuesta Rápida',
    priorityService: isEn ? 'Priority service' : 'Prioridad en atención',
    maxBenefits: isEn ? 'Maximum Benefits' : 'Beneficios Máximos',
    bestDiscounts: isEn ? 'The best discounts on the market' : 'Los mejores descuentos del mercado',
    activateVipMembership: isEn ? 'ACTIVATE VIP MEMBERSHIP' : 'ACTIVAR MEMBRESÍA VIP',
    // Testimonials section
    whatMembersSay: isEn ? 'What our members say' : 'Lo que dicen nuestros miembros',
    // FAQs
    faqTitle: isEn ? 'Frequently asked questions' : 'Preguntas frecuentes',
    faq1Q: isEn ? 'How long does the membership last?' : '¿Cuánto dura la membresía?',
    faq1A: isEn ? 'The membership lasts 12 months from the activation date.' : 'La membresía tiene una duración de 12 meses desde el momento de activación.',
    faq2Q: isEn ? 'Can I change plans later?' : '¿Puedo cambiar de plan después?',
    faq2A: isEn ? 'Yes, you can upgrade at any time by paying the difference.' : 'Sí, podés subir de plan en cualquier momento pagando la diferencia.',
    faq3Q: isEn ? 'How do I claim my monthly benefits?' : '¿Cómo reclamo mis beneficios mensuales?',
    faq3A: isEn ? 'You coordinate directly via WhatsApp with your advisor or by visiting any branch.' : 'Coordinás directamente por WhatsApp con tu asesor o visitando cualquier sucursal.',
    faq4Q: isEn ? 'Does the discount apply to the first purchase?' : '¿El descuento se aplica en la primera compra?',
    faq4A: isEn ? 'Yes! The discount applies immediately to your current purchase when activating the membership.' : '¡Sí! El descuento se aplica inmediatamente en tu compra actual al activar la membresía.',
    faq5Q: isEn ? 'Is there limited availability?' : '¿Hay disponibilidad limitada?',
    faq5A: isEn ? 'Yes. Only 50 memberships available per plan until January 31 at launch price.' : 'Sí. Solo hay 50 membresías disponibles por plan hasta el 31 de enero con precio de lanzamiento.',
    // Final CTA
    timeRemaining: isEn ? 'Time remaining of the promotion:' : 'Tiempo restante de la promoción:',
    only50PerPlan: isEn ? '⏰ Only 50 memberships per plan left' : '⏰ Solo quedan 50 membresías por plan',
    readyToSave: isEn ? 'Ready to ' : '¿Listo para ',
    saveSeriously: isEn ? 'save seriously' : 'ahorrar en serio',
    activateNowCta: isEn ? 'Activate your membership now and start enjoying your benefits TODAY.' : 'Activá tu membresía ahora y empezá a disfrutar tus beneficios HOY MISMO.',
    activateMyMembership: isEn ? 'ACTIVATE MY MEMBERSHIP' : 'ACTIVAR MI MEMBRESÍA',
    promoValidUntil: isEn ? 'Promotion valid until January 31 or while supplies last.' : 'Promoción válida hasta el 31 de enero o hasta agotar existencias.',
    // Portal section
    portalExclusive: isEn ? 'Exclusive portal' : 'Portal exclusivo',
    manageFromPortal: isEn ? 'Manage your Membership' : 'Manejá tu Membresía',
    portalDescription: isEn ? 'Check your benefits from your phone' : 'Consultá tus beneficios desde tu celular',
    accessPortal: isEn ? 'Access Portal' : 'Acceder al Portal',
    // Features translations
    temperedGlass1: isEn ? '1 Simple Tempered or Hydrogel HD per month' : '1 Temperado sencillo o Hidrogel HD por mes',
    temperedGlass2: isEn ? '2 Complete Tempered or 2 Hydrogel Mate or Restorative per month' : '2 Temperados completos o 2 Hidrogel Mate o Restaurativo por mes',
    hydrogelPremium2: isEn ? '2 Private Complete Tempered or 2 Premium Hydrogel per month' : '2 Temperados completo Privado o 2 Hidrogel Premium por mes',
    spaceCase1: isEn ? '1 Space Case per month' : '1 Estuche Space por mes',
    spaceCase2: isEn ? '2 Space MagSafe Cases per month' : '2 Estuches Space MagSafe por mes',
    magsafeCase: isEn ? '1 Premium or MagSafe Case per month' : '1 Estuche Premium o MagSafe por mes',
    cleanings2: isEn ? '2 Cleanings per month' : '2 Limpiezas por mes',
    cleanings3: isEn ? '3 Cleanings per month' : '3 Limpiezas por mes',
    warranty24h: isEn ? '24 hour tempered warranty' : 'Garantía temperado 24 horas',
    warranty3d: isEn ? '3 day tempered warranty' : 'Garantía temperado 3 días',
    warranty5d: isEn ? '5 day tempered warranty' : 'Garantía temperado 5 días',
    discount5_10: isEn ? '5-10% discount on products' : '5-10% descuento en productos',
    discount10_15: isEn ? '10-15% discount on products' : '10-15% descuento en productos',
    discount10_20: isEn ? '10-20% discount on products' : '10-20% descuento en productos',
    discountCasesGlass10: isEn ? '10% on cases and glass' : '10% en estuches y vidrios',
    discountCasesGlass15: isEn ? '15% on cases and glass' : '15% en estuches y vidrios',
    discountCasesGlass20: isEn ? '20% on cases and glass' : '20% en estuches y vidrios',
    discountRepairs10: isEn ? '10% on repairs' : '10% en reparaciones',
    discountRepairs15: isEn ? '15% on repairs' : '15% en reparaciones',
    discountRepairs20: isEn ? '20% on repairs' : '20% en reparaciones',
    preferentialService: isEn ? 'Preferential service' : 'Atención preferencial',
    vipExclusiveService: isEn ? 'VIP exclusive service' : 'Atención VIP exclusiva',
    freeShipping: isEn ? 'Free shipping' : 'Envío gratis',
    personalizedWhatsapp: isEn ? 'Personalized WhatsApp' : 'WhatsApp personalizado',
    // Plan messages
    bronzeMessage: isEn ? 'You protect your phone every month and everything is cheaper.' : 'Protegés tu celular todos los meses y todo te sale más barato.',
    silverMessage: isEn ? 'You pay less on every purchase and have real benefits every month.' : 'Pagás menos en cada compra y tenés beneficios reales todos los meses.',
    goldMessage: isEn ? 'You have direct line with VillaTech and benefits no one else has.' : 'Tenés línea directa con VillaTech y beneficios que nadie más tiene.',
    // Comparison table labels
    launchPriceLabel: isEn ? 'Launch price' : 'Precio de lanzamiento',
    annualPriceLabel: isEn ? 'Annual price' : 'Precio anual',
    glassHydrogel: isEn ? 'Glass / hydrogel' : 'Vidrios / hidrogel',
    temperedLabel: isEn ? 'tempered' : 'temperado',
    temperedPlural: isEn ? 'tempered' : 'temperados',
    premiumHydrogel: isEn ? 'premium hydrogel' : 'hidrogel premium',
    cases: isEn ? 'Cases' : 'Estuches',
    space: isEn ? 'Space' : 'Space',
    magsafePremium: isEn ? 'MagSafe/Premium' : 'MagSafe/Premium',
    cleaningsLabel: isEn ? 'Cleanings' : 'Limpiezas',
    perMonthLabel: isEn ? 'per month' : 'por mes',
    temperedWarranty: isEn ? 'Tempered warranty' : 'Garantía de temperado',
    hoursLabel: isEn ? 'hours' : 'horas',
    daysLabel: isEn ? 'days' : 'días',
    preferentialLabel: isEn ? 'Preferential' : 'Preferencial',
    vipExclusive: isEn ? 'VIP exclusive' : 'VIP exclusiva',
    personalizedWhatsappLabel: isEn ? 'Personalized WhatsApp' : 'WhatsApp personalizado',
    direct: isEn ? 'Direct' : 'Directo',
    freeShippingLabel: isEn ? 'Free Shipping' : 'Envío Gratis',
    discCasesTempered: isEn ? 'Disc. cases and tempered' : 'Desc. estuches y temperados',
    discGeneralTech: isEn ? 'Disc. general technology' : 'Desc. tecnología general',
    discRepairs: isEn ? 'Disc. repairs' : 'Desc. reparaciones',
    // Categories
    casesAndGlass: isEn ? 'Cases and glass' : 'Estuches y vidrios',
    chargersAndCables: isEn ? 'Chargers and cables' : 'Cargadores y cables',
    technology: isEn ? 'Technology' : 'Tecnología',
    gaming: isEn ? 'Gaming' : 'Gaming',
    repairs: isEn ? 'Repairs' : 'Reparaciones',
    // Testimonials
    testimonial1: isEn ? "I paid ₡12,000 for the Bronze membership, and that same day I saved ₡7,000 on my Alexa!" : "Pagué ₡12.000 por la membresía Bronce, ¡y ese mismo día me ahorré ₡7.000 en mi Alexa!",
    testimonial2: isEn ? "Every month I come for my cleaning and case. I don't spend on those things anymore." : "Cada mes vengo por mi limpieza y estuche. Ya no gasto más en esas cosas.",
    testimonial3: isEn ? "With GOLD I have 20% on gaming and I talk directly to the advisor. I already recovered my investment." : "Con ORO tengo 20% en gaming y hablo directo con el asesor. Ya recuperé la inversión.",
    bronzeMember: isEn ? 'Bronze Member' : 'Miembro Bronce',
    silverMember: isEn ? 'Silver Member' : 'Miembro Plata',
    goldMember: isEn ? 'Gold Member' : 'Miembro Oro',
    year: isEn ? 'year' : 'año',
    month: isEn ? 'month' : 'mes',
  };

  // Facebook Pixel tracking
  const { trackAddToCart, trackInitiateCheckout, trackViewContent } = useFacebookPixel();
  const hasTrackedViewContent = useRef(false);

  // Track ViewContent when user scrolls to #planes section
  useEffect(() => {
    const planesSection = document.getElementById('planes');
    if (!planesSection || hasTrackedViewContent.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTrackedViewContent.current) {
          hasTrackedViewContent.current = true;
          trackViewContent({
            content_name: 'Planes de Membresía',
            content_category: 'Membresías',
          });
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(planesSection);
    return () => observer.disconnect();
  }, [trackViewContent]);

  const openCardForm = (plan: any) => {
    // Track Facebook Pixel events
    const planPrice = typeof plan.launchPrice === 'number' ? plan.launchPrice : 0;
    trackAddToCart({
      content_name: `Membresía ${plan.name}`,
      content_ids: [plan.id],
      value: planPrice,
      currency: 'CRC',
    });
    trackInitiateCheckout({
      content_name: `Membresía ${plan.name}`,
      value: planPrice,
      currency: 'CRC',
    });
    
    setSelectedPlan(plan);
    setIsFormOpen(true);
  };

  const handleFormSubmit = (formData: any, plan: any) => {
    // This is now handled internally by MembershipCardForm
    // which redirects to /registro-membresia
  };

  const scrollToPlans = () => {
    document.getElementById('planes')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Pasos del proceso
  const steps = [
    { 
      number: "1", 
      title: t.step1Title, 
      description: t.step1Desc,
      image: stepRegister
    },
    { 
      number: "2", 
      title: t.step2Title, 
      description: t.step2Desc,
      image: stepWhatsapp
    },
    { 
      number: "3", 
      title: t.step3Title, 
      description: t.step3Desc,
      image: stepBenefits
    },
  ];

  // Descuentos visuales por categoría con imágenes - PRODUCTOS REALES VILLATECH
  const discountVisuals = {
    bronce: [
      { category: 'Gaming', discount: '5%', image: gameProjector, color: 'from-amber-700 to-amber-900' },
      { category: isEn ? 'Cases' : 'Estuches', discount: '5%', image: samsungLeatherCase, color: 'from-amber-700 to-amber-900' },
      { category: isEn ? 'Tech' : 'Tecnología', discount: '5%', image: droneE88, color: 'from-amber-700 to-amber-900' },
      { category: isEn ? 'Cables' : 'Cargadores', discount: '5%', image: magneticCharger, color: 'from-amber-700 to-amber-900' },
      { category: 'Audio', discount: '5%', image: metalStand, color: 'from-amber-700 to-amber-900' },
      { category: isEn ? 'Repairs' : 'Reparaciones', discount: '10%', image: 'https://images.unsplash.com/photo-1597329579763-9e46f247f538?w=400', color: 'from-amber-700 to-amber-900' },
    ],
    plata: [
      { category: 'Gaming', discount: '10%', image: gameProjector, color: 'from-slate-400 to-slate-600' },
      { category: isEn ? 'Cases' : 'Estuches', discount: '10%', image: smartKeyboardCase, color: 'from-slate-400 to-slate-600' },
      { category: isEn ? 'Tech' : 'Tecnología', discount: '10%', image: droneK12Max, color: 'from-slate-400 to-slate-600' },
      { category: isEn ? 'Cables' : 'Cargadores', discount: '10%', image: magneticCharger, color: 'from-slate-400 to-slate-600' },
      { category: 'Audio', discount: '10%', image: metalStand, color: 'from-slate-400 to-slate-600' },
      { category: 'Power Bank', discount: '10%', image: fengaiC60, color: 'from-slate-400 to-slate-600' },
      { category: isEn ? 'Repairs' : 'Reparaciones', discount: '15%', image: 'https://images.unsplash.com/photo-1597329579763-9e46f247f538?w=400', color: 'from-slate-400 to-slate-600' },
    ],
    oro: [
      { category: 'Gaming', discount: '20%', image: gameProjector, color: 'from-yellow-500 to-amber-600' },
      { category: isEn ? 'Premium Cases' : 'Estuches Premium', discount: '20%', image: smartKeyboardCase, color: 'from-yellow-500 to-amber-600' },
      { category: isEn ? 'Tech' : 'Tecnología', discount: '15%', image: droneK12Max, color: 'from-yellow-500 to-amber-600' },
      { category: isEn ? 'Cables' : 'Cargadores', discount: '20%', image: magneticCharger, color: 'from-yellow-500 to-amber-600' },
      { category: 'Audio', discount: '15%', image: metalStand, color: 'from-yellow-500 to-amber-600' },
      { category: 'Power Bank', discount: '20%', image: fengaiC60, color: 'from-yellow-500 to-amber-600' },
      { category: isEn ? 'Drones' : 'Drones', discount: '10%', image: droneE88, color: 'from-yellow-500 to-amber-600' },
      { category: isEn ? 'Repairs' : 'Reparaciones', discount: '20%', image: 'https://images.unsplash.com/photo-1597329579763-9e46f247f538?w=400', color: 'from-yellow-500 to-amber-600' },
    ],
  };

  // Planes de membresía (sin datos de costos internos)
  const plans = [
    {
      id: 'bronce',
      name: t.bronze,
      subtitle: t.basicProtection,
      regularPrice: 15000,
      launchPrice: 12000,
      annualPrice: 120000, // 10 meses (2 gratis)
      color: 'amber',
      colorHex: '#b45309',
      icon: Shield,
      logo: logoBronce,
      cardLogo: cardLogoBronce,
      cardImage: cardBronce,
      emoji: '🟫',
      discounts: discountVisuals.bronce,
      features: [
        { text: t.temperedGlass1, included: true },
        { text: t.spaceCase1, included: true },
        { text: t.cleanings2, included: true },
        { text: t.warranty24h, included: true },
        { text: t.preferentialService, included: false },
        { text: t.freeShipping, included: false },
        { text: 'WhatsApp VIP', included: false },
      ],
      message: t.bronzeMessage,
    },
    {
      id: 'plata',
      name: t.silver,
      subtitle: t.completeProtection,
      regularPrice: 30000,
      launchPrice: 27000,
      annualPrice: 270000, // 10 meses (2 gratis)
      color: 'slate',
      colorHex: '#64748b',
      icon: Star,
      logo: logoPlata,
      cardLogo: cardLogoPlata,
      cardImage: cardSilver,
      emoji: '🥈',
      popular: true,
      discounts: discountVisuals.plata,
      features: [
        { text: t.temperedGlass2, included: true },
        { text: t.spaceCase2, included: true },
        { text: t.cleanings2, included: true },
        { text: t.warranty3d, included: true },
        { text: t.preferentialService, included: true },
        { text: `${t.freeShipping} +₡40.000`, included: true },
        { text: 'WhatsApp VIP', included: false },
      ],
      message: t.silverMessage,
    },
    {
      id: 'oro',
      name: t.gold,
      subtitle: t.vipTotal,
      regularPrice: 50000,
      launchPrice: 45000,
      annualPrice: 450000, // 10 meses (2 gratis)
      color: 'yellow',
      colorHex: '#eab308',
      icon: Crown,
      logo: logoOro,
      cardLogo: cardLogoOro,
      cardImage: cardGold,
      emoji: '🥇',
      vip: true,
      discounts: discountVisuals.oro,
      features: [
        { text: t.hydrogelPremium2, included: true },
        { text: t.magsafeCase, included: true },
        { text: t.cleanings3, included: true },
        { text: t.warranty5d, included: true },
        { text: t.vipExclusiveService, included: true },
        { text: `${t.freeShipping} +₡20.000`, included: true },
        { text: t.personalizedWhatsapp, included: true },
      ],
      message: t.goldMessage,
    }
  ];

  // Descuentos por categoría - ACTUALIZADO
  const discountCategories = [
    { category: t.gaming, bronce: "5%", plata: "10%", oro: "20%", icon: "🎮" },
    { category: isEn ? "Basic Cases" : "Estuches básicos", bronce: "5%", plata: "10%", oro: "20%", icon: "📱" },
    { category: isEn ? "Tempered Glass" : "Vidrios templados", bronce: "5%", plata: "10%", oro: "15%", icon: "🛡️" },
    { category: isEn ? "Hydrogel HD" : "Hidrogel HD", bronce: "—", plata: "10%", oro: "15%", icon: "🛡️" },
    { category: isEn ? "Premium Cases (MagSafe, OtterBox)" : "Estuches premium (MagSafe, OtterBox)", bronce: "—", plata: "10%", oro: "20%", icon: "📱" },
    { category: t.chargersAndCables, bronce: "5%", plata: "10%", oro: "20%", icon: "⚡" },
    { category: isEn ? "Basic Audio" : "Audio económico", bronce: "5%", plata: "10%", oro: "15%", icon: "🎧" },
    { category: isEn ? "Streaming (Alexa, Fire TV, Roku, Chromecast)" : "Streaming (Alexa, Fire TV, Roku, Chromecast)", bronce: "—", plata: "10%", oro: "20%", icon: "📺" },
    { category: isEn ? "Smart Watches" : "Relojes inteligentes", bronce: "—", plata: "—", oro: "10%", icon: "⌚" },
    { category: t.repairs, bronce: "10%", plata: "15%", oro: "20%", icon: "🛠️" },
  ];

  // Productos con precios por plan - PRODUCTOS REALES VILLATECH
  const productsByCategory = [
    { 
      category: t.gaming, 
      categoryKey: 'Gaming',
      icon: "🎮",
      products: [
        { name: isEn ? "Game Projector 2 in 1" : "Proyector Gaming 2 en 1", price: 66000, image: gameProjector },
        { name: "Drone K12MAX", price: 46000, image: droneK12Max },
        { name: "Drone E88", price: 22000, image: droneE88 },
      ]
    },
    { 
      category: t.casesAndGlass, 
      categoryKey: 'Estuches y vidrios',
      icon: "📱",
      products: [
        { name: isEn ? "Samsung Leather Case" : "Samsung Leather Case", price: 10000, image: samsungLeatherCase },
        { name: isEn ? "Smart Keyboard Case" : "Smart Keyboard Case", price: 14000, image: smartKeyboardCase },
        { name: isEn ? "Metal Rotatory Stand" : "Metal Rotatory Stand RM-C38", price: 10000, image: metalStand },
      ]
    },
    { 
      category: t.chargersAndCables, 
      categoryKey: 'Cargadores y cables',
      icon: "⚡",
      products: [
        { name: isEn ? "Magnetic Wireless Charger" : "Cargador Magnético Remax", price: 19000, image: magneticCharger },
        { name: isEn ? "Metal Rotatory Stand" : "Metal Rotatory Stand RM-C38", price: 10000, image: metalStand },
        { name: isEn ? "Fengai C60 Power Bank" : "Fengai C60 Power Bank", price: 54000, image: fengaiC60 },
      ]
    },
    { 
      category: t.technology, 
      categoryKey: 'Tecnología',
      icon: "📺",
      products: [
        { name: "Drone E88", price: 22000, image: droneE88 },
        { name: "Drone K12MAX", price: 46000, image: droneK12Max },
        { name: isEn ? "Game Projector 2 in 1" : "Proyector Gaming 2 en 1", price: 66000, image: gameProjector },
      ]
    },
    { 
      category: t.repairs, 
      categoryKey: 'Reparaciones',
      icon: "🛠️",
      products: [
        { name: isEn ? "Screen Replacement" : "Cambio de Pantalla", price: 45000, image: 'https://images.unsplash.com/photo-1597329579763-9e46f247f538?w=400' },
        { name: isEn ? "Battery Replacement" : "Cambio de Batería", price: 25000, image: 'https://images.unsplash.com/photo-1621330396173-e41b1cafd17f?w=400' },
        { name: isEn ? "Deep Cleaning" : "Limpieza Profunda", price: 8000, image: 'https://images.unsplash.com/photo-1581993192873-7a5eb7f40b94?w=400' },
      ]
    },
  ];

  // Descuentos por plan y categoría - ACTUALIZADO
  const discountRates: { [key: string]: { [key: string]: number } } = {
    'bronce': { 'Estuches y vidrios': 5, 'Cargadores y cables': 5, 'Tecnología': 5, 'Gaming': 5, 'Audio': 5, 'Reparaciones': 10 },
    'plata': { 'Estuches y vidrios': 10, 'Cargadores y cables': 10, 'Tecnología': 10, 'Gaming': 10, 'Audio': 10, 'Reparaciones': 15 },
    'oro': { 'Estuches y vidrios': 20, 'Cargadores y cables': 20, 'Tecnología': 20, 'Gaming': 20, 'Audio': 15, 'Reparaciones': 20 },
  };

  const calculateDiscountedPrice = (price: number, category: string, plan: string) => {
    const discount = discountRates[plan]?.[category] || 0;
    return Math.round(price * (1 - discount / 100));
  };

  const formatPrice = (price: number) => `₡${price.toLocaleString('es-CR')}`;

  // Testimonios
  const testimonials = [
    {
      text: t.testimonial1,
      name: "Laura R.",
      role: t.bronzeMember,
      image: "https://randomuser.me/api/portraits/women/44.jpg"
    },
    {
      text: t.testimonial2,
      name: "Josué T.",
      role: t.silverMember,
      image: "https://randomuser.me/api/portraits/men/32.jpg"
    },
    {
      text: t.testimonial3,
      name: "Paola M.",
      role: t.goldMember,
      image: "https://randomuser.me/api/portraits/women/68.jpg"
    },
  ];

  // FAQs
  const faqs = [
    {
      question: t.faq1Q,
      answer: t.faq1A
    },
    {
      question: t.faq2Q,
      answer: t.faq2A
    },
    {
      question: t.faq3Q,
      answer: t.faq3A
    },
    {
      question: t.faq4Q,
      answer: t.faq4A
    },
    {
      question: t.faq5Q,
      answer: t.faq5A
    },
  ];

  // Tabla comparativa (función para generar dinámicamente según el período)
  const getComparisonRows = () => {
    const priceRow = billingPeriod === 'monthly' 
      ? { label: t.launchPriceLabel, bronce: `${formatPrice(12000)}/${t.month}`, plata: `${formatPrice(27000)}/${t.month}`, oro: `${formatPrice(45000)}/${t.month}` }
      : { label: t.annualPriceLabel, bronce: `${formatPrice(120000)}/${t.year}`, plata: `${formatPrice(270000)}/${t.year}`, oro: `${formatPrice(450000)}/${t.year}` };
    
    return [
      priceRow,
      { label: t.glassHydrogel, bronce: `1 ${t.temperedLabel}`, plata: `2 ${t.temperedPlural}`, oro: `2 ${t.premiumHydrogel}` },
      { label: t.cases, bronce: `1 ${t.space}`, plata: `2 ${t.space}`, oro: `1 ${t.magsafePremium}` },
      { label: t.cleaningsLabel, bronce: `2 ${t.perMonthLabel}`, plata: `2 ${t.perMonthLabel}`, oro: `3 ${t.perMonthLabel}` },
      { label: t.temperedWarranty, bronce: `24 ${t.hoursLabel}`, plata: `3 ${t.daysLabel}`, oro: `5 ${t.daysLabel}` },
      { label: t.preferentialService, bronce: "—", plata: t.preferentialLabel, oro: t.vipExclusive },
      { label: t.personalizedWhatsappLabel, bronce: "—", plata: "—", oro: `✔️ ${t.direct}` },
      { label: t.freeShippingLabel, bronce: "—", plata: "+₡40.000", oro: "+₡20.000" },
      { label: t.gaming, bronce: "5%", plata: "10%", oro: "20%" },
      { label: t.discCasesTempered, bronce: "5%", plata: "10%", oro: "20%" },
      { label: "Hidrogel HD / Premium", bronce: "—", plata: "10%", oro: "15%" },
      { label: t.chargersAndCables, bronce: "5%", plata: "10%", oro: "20%" },
      { label: "Audio", bronce: "5%", plata: "10%", oro: "15%" },
      { label: "Streaming (Alexa, Fire TV, Roku)", bronce: "—", plata: "10%", oro: "20%" },
      { label: "Relojes inteligentes", bronce: "—", plata: "—", oro: "10%" },
      { label: t.discRepairs, bronce: "10%", plata: "15%", oro: "20%" },
    ];
  };
  
  const comparisonRows = getComparisonRows();

  // Animation variants for staggered page entrance
  const pageVariants = {
    hidden: { opacity: 0, filter: 'blur(10px)' },
    visible: { 
      opacity: 1, 
      filter: 'blur(0px)',
      transition: { duration: 0.8, ease: [0, 0, 0.2, 1] as const }
    }
  };

  const sectionVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: (delay: number) => ({
      opacity: 1,
      y: 0,
      transition: { 
        duration: 0.6, 
        ease: [0, 0, 0.2, 1] as const,
        delay 
      }
    })
  };

  return (
    <motion.div 
      className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 text-white overflow-x-hidden relative"
      initial="hidden"
      animate="visible"
      variants={pageVariants}
    >
      {/* Background glow effects - consistent with all pages */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-3xl" />
      </div>
      
      {/* HERO SECTION - First to appear */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.7, ease: 'easeOut' }}
      >
        <HeroSectionWithParallax 
          t={t} 
          timeLeft={timeLeft}
          plans={plans}
          scrollToPlans={scrollToPlans}
        />
      </motion.div>

      {/* QUÉ INCLUYE - Benefits Showcase */}
      <motion.section 
        className="py-16 relative z-10"
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        custom={0.2}
      >
        <div className="container mx-auto px-6">
          <MembershipBenefitsShowcase 
            isOpen={true} 
            onClose={() => {}} 
          />
        </div>
      </motion.section>

      {/* CÓMO FUNCIONA */}
      <motion.section 
        className="py-20 relative z-10"
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        custom={0.3}
      >
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              {t.howItWorks} <span className="text-blue-400">{t.superEasy}</span>
            </h2>
            <p className="text-zinc-400 text-lg">{t.stepsDesc}</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto items-start">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group relative"
              >
                {/* Neumorphic Card */}
                <div className="relative bg-zinc-800/40 rounded-2xl overflow-hidden
                  shadow-[8px_8px_16px_rgba(0,0,0,0.4),-4px_-4px_12px_rgba(255,255,255,0.05)]
                  border border-zinc-700/30
                  hover:shadow-[12px_12px_24px_rgba(0,0,0,0.5),-6px_-6px_16px_rgba(255,255,255,0.08)]
                  transition-all duration-300 hover:-translate-y-1"
                >
                  {/* Image */}
                  <div className="relative overflow-hidden">
                    <img 
                      src={step.image} 
                      alt={step.title}
                      className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/30 to-transparent" />
                    
                    {/* Step number badge */}
                    <div className="absolute top-3 left-3 w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 
                      flex items-center justify-center text-white font-bold text-lg
                      shadow-[4px_4px_8px_rgba(0,0,0,0.3),-2px_-2px_6px_rgba(255,255,255,0.1)]"
                    >
                      {step.number}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6 text-center">
                    <h3 className="text-lg font-bold text-white mb-2">{step.title}</h3>
                    <p className="text-zinc-400 text-sm">{step.description}</p>
                  </div>

                  {/* Glow effect on hover */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-300 bg-gradient-to-br from-blue-500 to-blue-600 pointer-events-none" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* TARJETAS ESTILO CRÉDITO */}
      <motion.section 
        className="py-20 bg-gradient-to-b from-zinc-900 to-black"
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        custom={0.4}
      >
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              <span className="text-white">{t.yourVillatechCard}</span>
            </h2>
            <p className="text-zinc-400 text-lg">{t.chooseLevel}</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-20">
            {plans.map((plan, index) => {
              const isGold = plan.id === 'oro';
              
              return (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 30, rotateY: -15 }}
                  whileInView={{ opacity: 1, y: 0, rotateY: 0 }}
                  whileHover={{ scale: 1.05, rotateY: 5 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15, duration: 0.5 }}
                  onClick={() => {
                    const planElement = document.getElementById(`plan-${plan.id}`);
                    planElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }}
                  className={`cursor-pointer perspective-1000 ${isGold ? 'relative' : ''}`}
                >
                  {/* Glow effect for gold card */}
                  {isGold && (
                    <div className="absolute -inset-2 bg-gradient-to-r from-blue-400/40 via-blue-300/30 to-blue-400/40 rounded-3xl blur-xl opacity-60" />
                  )}
                  
                  {/* Card Image */}
                  <div className="relative w-full aspect-[1.6/1] bg-transparent overflow-hidden rounded-2xl">
                    <img 
                      src={plan.cardImage} 
                      alt={`${t.membership} ${plan.name}`}
                      className="w-full h-full object-contain drop-shadow-[0_20px_50px_rgba(0,0,0,0.5)]"
                      style={{ background: 'transparent' }}
                    />
                    
                    {/* VIP badge for oro */}
                    {plan.vip && (
                      <div className="absolute top-4 left-1/2 -translate-x-1/2">
                        <Badge className="bg-black/50 text-blue-400 border-blue-500/50 text-xs backdrop-blur-sm">
                          <Crown className="w-3 h-3 mr-1" /> VIP
                        </Badge>
                      </div>
                    )}
                  </div>
                  
                  {/* Click to activate text */}
                  <div className="text-center mt-4">
                    <p className="text-zinc-500 text-sm">Click para ver precios</p>
                  </div>
                </motion.div>
              );
            })}
          </div>


          {/* Card Form Modal */}
          {selectedPlan && (
            <MembershipCardForm
              plan={selectedPlan}
              isOpen={isFormOpen}
              onClose={() => setIsFormOpen(false)}
              onSubmit={handleFormSubmit}
              billingPeriod={billingPeriod === 'monthly' ? 'mensual' : 'anual'}
            />
          )}
        </div>
      </motion.section>

      {/* MIRÁ CUÁNTO AHORRÁS */}
      <motion.section 
        className="py-20 relative z-10"
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        custom={0.5}
      >
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              💰 <span className="text-white">Mirá cuánto ahorrás</span>
            </h2>
            <p className="text-zinc-400 text-lg mb-8">Seleccioná un plan y mirá los precios con descuento</p>
            
            {/* Plan selector for discounts - with logos */}
            <div className="flex justify-center gap-4 mb-8">
              {(['bronce', 'plata', 'oro'] as const).map((planKey) => {
                const planLogos: { [key: string]: string } = {
                  bronce: logoBronce,
                  plata: logoPlata,
                  oro: logoOro,
                };
                const isSelected = selectedDiscountPlan === planKey;
                
                return (
                  <motion.button
                    key={planKey}
                    onClick={() => setSelectedDiscountPlan(planKey)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={`p-3 rounded-xl transition-all duration-300 ${
                      isSelected
                        ? 'bg-zinc-800 border-2 border-blue-500 shadow-[0_0_20px_rgba(59,130,246,0.3)]'
                        : 'bg-zinc-800/50 border border-zinc-700 hover:border-zinc-500'
                    }`}
                  >
                    <img 
                      src={planLogos[planKey]} 
                      alt={planKey} 
                      className="h-10 w-auto"
                    />
                  </motion.button>
                );
              })}
            </div>
          </motion.div>

          {/* Infinite Category Slider */}
          <InfiniteCategorySlider
            categories={productsByCategory}
            selectedPlan={selectedDiscountPlan}
            discountRates={discountRates}
            formatPrice={formatPrice}
            calculateDiscountedPrice={calculateDiscountedPrice}
          />
        </div>
      </motion.section>


      {/* PLANES DE MEMBRESÍA */}
      <motion.section 
        id="planes" 
        className="py-20 relative z-10"
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        custom={0.6}
      >
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              <span className="text-blue-400">Elegí tu membresía</span>
            </h2>
            <p className="text-zinc-400 text-lg mb-6">Precios promocionales hasta el 31 de enero 2026</p>
            
            {/* Toggle Mensual / Anual */}
            <div className="flex justify-center items-center gap-4 mb-8">
              <span className={`text-lg font-medium transition-colors ${billingPeriod === 'monthly' ? 'text-blue-400' : 'text-zinc-500'}`}>
                Mensual
              </span>
              <button
                onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'annual' : 'monthly')}
                className="relative w-20 h-10 rounded-full bg-zinc-800 border border-zinc-600 p-1 transition-colors"
              >
                <motion.div
                  className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-400 to-blue-600 shadow-lg"
                  animate={{ x: billingPeriod === 'annual' ? 40 : 0 }}
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                />
              </button>
              <div className="flex flex-col md:flex-row items-center gap-1 md:gap-2">
                <span className={`text-lg font-medium transition-colors ${billingPeriod === 'annual' ? 'text-blue-400' : 'text-zinc-500'}`}>
                  Anual
                </span>
                <Badge className="bg-green-500/20 text-green-400 border-green-500/40 text-xs">
                  2 meses gratis
                </Badge>
              </div>
            </div>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {plans.map((plan, index) => {
              const Icon = plan.icon;
              return (
                <motion.div
                  key={plan.id}
                  id={`plan-${plan.id}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`relative rounded-3xl overflow-hidden h-full flex flex-col ${
                    plan.vip 
                      ? 'bg-gradient-to-b from-blue-500/20 to-zinc-900 border-2 border-blue-500/50 shadow-[0_0_40px_rgba(59,130,246,0.2)]' 
                      : 'bg-zinc-900 border border-zinc-700'
                  }`}
                >
                  {/* Badge */}
                  {plan.popular && (
                    <div className="absolute -top-0 left-1/2 -translate-x-1/2 z-10">
                      <Badge className="bg-slate-400 text-black px-4 py-1 font-bold rounded-b-lg rounded-t-none">
                        MÁS POPULAR
                      </Badge>
                    </div>
                  )}
                  {plan.vip && (
                    <div className="absolute -top-0 left-1/2 -translate-x-1/2 z-10">
                      <Badge className="bg-gradient-to-r from-yellow-500 to-amber-600 text-white px-4 py-1 font-bold rounded-b-lg rounded-t-none shadow-lg">
                        🔥 50 DISPONIBLES
                      </Badge>
                    </div>
                  )}

                  <div className="p-8 pt-12 flex flex-col flex-grow">
                    {/* Card Image - Larger size */}
                    <div className="mb-8">
                      <div className="relative mx-auto max-w-[340px] aspect-[1.6/1] bg-transparent overflow-hidden rounded-2xl">
                        <img 
                          src={plan.cardImage} 
                          alt={`Membresía ${plan.name}`}
                          className="w-full h-full object-contain drop-shadow-[0_15px_30px_rgba(0,0,0,0.5)]"
                          style={{ background: 'transparent' }}
                        />
                      </div>
                      <p className="text-zinc-400 text-center mt-3">{plan.subtitle}</p>
                    </div>

                    {/* Precio */}
                    <div className="text-center mb-6">
                      {billingPeriod === 'monthly' ? (
                        <>
                          <div className="text-zinc-500 line-through text-lg">{formatPrice(plan.regularPrice)} por mes</div>
                          <div className="text-4xl font-bold text-white">{formatPrice(plan.launchPrice)}</div>
                          <div className="text-zinc-400">por mes</div>
                        </>
                      ) : (
                        <>
                          <div className="text-zinc-500 line-through text-lg">{formatPrice(plan.launchPrice * 12)}/año</div>
                          <div className="text-4xl font-bold text-white">{formatPrice(plan.annualPrice)}</div>
                          <div className="text-zinc-400">/año</div>
                          <Badge className="mt-2 bg-green-500/20 text-green-400 border-green-500/40">
                            Ahorrás {formatPrice(plan.launchPrice * 12 - plan.annualPrice)}
                          </Badge>
                        </>
                      )}
                    </div>

                    {/* Discount Categories with Images - LARGER & FLOATING */}
                    <div className="mb-8">
                      <p className="text-base text-zinc-300 text-center mb-4 font-semibold">
                        {isEn ? '🏷️ Your discounts:' : '🏷️ Tus descuentos:'}
                      </p>
                      <div className="grid grid-cols-3 gap-3">
                        {plan.discounts?.slice(0, 6).map((discount: any, i: number) => (
                          <div
                            key={i}
                            className="relative group cursor-pointer transition-transform duration-200 hover:scale-105 hover:-translate-y-1"
                          >
                            <div 
                              className={`relative rounded-2xl overflow-hidden aspect-square 
                                shadow-[0_8px_30px_rgba(0,0,0,0.4)] 
                                border-2 border-white/10
                                group-hover:shadow-[0_15px_40px_rgba(0,0,0,0.5)]
                                transition-shadow duration-300`}
                            >
                              <img 
                                src={discount.image} 
                                alt={discount.category}
                                className="w-full h-full object-cover scale-110"
                              />
                              <div className={`absolute inset-0 bg-gradient-to-t ${discount.color} opacity-85`} />
                              <div className="absolute inset-0 flex flex-col items-center justify-center p-2">
                                <span className="text-white font-black text-2xl md:text-3xl drop-shadow-[0_3px_6px_rgba(0,0,0,0.9)]">
                                  {discount.discount}
                                </span>
                                <span className="text-white text-[10px] md:text-xs font-bold text-center leading-tight drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)] mt-1">
                                  {discount.category}
                                </span>
                              </div>
                              {/* Glow effect */}
                              <div className={`absolute -inset-1 opacity-0 group-hover:opacity-60 transition-opacity duration-300 bg-gradient-to-t ${discount.color} blur-xl -z-10 rounded-2xl`} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Phone Compatibility Visual */}
                    <div className="mb-6 flex items-center justify-center">
                      <div className="rounded-3xl bg-zinc-800/50 shadow-[inset_4px_4px_8px_rgba(0,0,0,0.4),inset_-4px_-4px_8px_rgba(255,255,255,0.05),8px_8px_16px_rgba(0,0,0,0.3),-4px_-4px_12px_rgba(255,255,255,0.03)] border border-zinc-700/30 overflow-hidden">
                        <img
                          src={plan.name.toLowerCase().includes('oro') ? phoneOroOtterbox : plan.name.toLowerCase().includes('plata') ? phonePlataMagsafe : phoneBronceClear}
                          alt={plan.name.toLowerCase().includes('oro') ? 'iPhone con OtterBox' : plan.name.toLowerCase().includes('plata') ? 'iPhone con MagSafe' : 'iPhone con estuche transparente'}
                          className="w-full max-w-[280px] h-auto aspect-square object-contain p-2"
                          loading="lazy"
                          decoding="async"
                        />
                      </div>
                    </div>

                    {/* Features */}
                    <ul className="space-y-3 mb-8">
                      {plan.features.map((feature: any, i: number) => (
                        <li key={i} className="flex items-start gap-3">
                          {feature.included ? (
                            <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                          ) : (
                            <X className="w-5 h-5 text-zinc-600 flex-shrink-0 mt-0.5" />
                          )}
                          <span className={feature.included ? 'text-white' : 'text-zinc-500'}>
                            {feature.text}
                          </span>
                        </li>
                      ))}
                    </ul>

                    {/* Spacer to push content to bottom */}
                    <div className="flex-grow" />

                    {/* Mensaje comercial */}
                    <p className="text-sm text-zinc-400 text-center mb-6 italic">
                      "{plan.message}"
                    </p>

                    {/* CTA */}
                    <Button
                      onClick={() => openCardForm(plan)}
                      className={`w-full py-6 text-lg font-bold rounded-xl ${
                        plan.vip
                          ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white'
                          : 'bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-600'
                      }`}
                    >
                      <CreditCard className="w-5 h-5 mr-2" />
                      Activar Membresía
                    </Button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </motion.section>


      {/* TABLA COMPARATIVA */}
      <motion.section 
        className="py-20 relative z-10"
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        custom={0.7}
      >
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              <span className="text-blue-400">Compará los planes</span>
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto bg-zinc-900 border border-zinc-700 rounded-3xl overflow-hidden
              shadow-[inset_0_2px_4px_rgba(255,255,255,0.05),0_8px_32px_rgba(0,0,0,0.6)]"
          >
            <div className="overflow-x-auto">
              <table className="w-full min-w-[500px]">
                <thead>
                  <tr className="bg-zinc-800">
                    <th className="text-left p-3 md:p-4 text-zinc-400 sticky left-0 bg-zinc-800 z-10 min-w-[100px]">Beneficio</th>
                    <th className="text-center p-3 md:p-4">
                      <img src={logoBronce} alt="Bronce" className="h-7 md:h-8 mx-auto" />
                    </th>
                    <th className="text-center p-3 md:p-4">
                      <img src={logoPlata} alt="Plata" className="h-7 md:h-8 mx-auto" />
                    </th>
                    <th className="text-center p-3 md:p-4">
                      <img src={logoOro} alt="Oro" className="h-7 md:h-8 mx-auto" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonRows.map((row, index) => (
                    <tr key={index} className="border-t border-zinc-700 hover:bg-zinc-800/50">
                      <td className="p-3 md:p-4 text-white font-medium text-sm md:text-base sticky left-0 bg-zinc-900 z-10 min-w-[100px]">{row.label}</td>
                      <td className="p-4 text-center">
                        {row.bronce === "—" ? (
                          <X className="w-5 h-5 text-zinc-600 mx-auto" />
                        ) : (
                          <span className="text-zinc-300">{row.bronce}</span>
                        )}
                      </td>
                      <td className="p-4 text-center">
                        {row.plata === "—" ? (
                          <X className="w-5 h-5 text-zinc-600 mx-auto" />
                        ) : (
                          <span className="text-zinc-300">{row.plata}</span>
                        )}
                      </td>
                      <td className="p-4 text-center">
                        {row.oro === "—" ? (
                          <X className="w-5 h-5 text-zinc-600 mx-auto" />
                        ) : (
                          <span className="text-blue-400 font-semibold">{row.oro}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* FAQs */}
      <motion.section 
        className="py-20 bg-gradient-to-b from-zinc-900 to-black"
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        custom={0.8}
      >
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              <span className="text-blue-400">Preguntas frecuentes</span>
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`faq-${index}`}
                  className="bg-zinc-900 border border-zinc-700 rounded-2xl px-6 overflow-hidden"
                >
                  <AccordionTrigger className="text-left text-white hover:text-blue-400 py-5">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-zinc-400 pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </motion.section>

      {/* CALCULADOR DE AHORRO PARA CLIENTES */}
      <CustomerSavingsCalculator onSelectPlan={openCardForm} plans={plans} />

      {/* CTA FINAL */}
      <motion.section 
        className="py-20 relative z-10"
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        custom={0.9}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-transparent to-blue-500/5" />
        
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            {/* Badge removed */}

            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              ¿Listo para <span className="text-blue-400">ahorrar en serio</span>?
            </h2>

            <p className="text-xl text-zinc-300 mb-8">
              Activá tu membresía ahora y empezá a disfrutar tus beneficios HOY MISMO.
            </p>

            <div className="flex justify-center">
              <Button
                onClick={scrollToPlans}
                size="lg"
                className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold text-lg md:text-xl px-8 md:px-12 py-6 md:py-8 rounded-2xl shadow-[0_0_40px_rgba(59,130,246,0.4)] hover:shadow-[0_0_60px_rgba(59,130,246,0.6)] transition-all duration-300"
              >
                <CreditCard className="w-5 h-5 md:w-6 md:h-6 mr-2 md:mr-3" />
                ACTIVAR MI MEMBRESÍA
              </Button>
            </div>

            <p className="text-zinc-500 mt-6 text-sm">
              Promoción válida hasta el 31 de enero o hasta agotar existencias.
            </p>
          </motion.div>
        </div>
      </motion.section>
    </motion.div>
  );
};

export default Membresias;
